/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { Box, Chip, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useAgentPanel } from '../../../agent/AgentPanelContext';
import AgentChatIcon from '../../../icons/ai-chat.svg?react';
import Widget from '../../../react-components/Widget';
import { useLastSimulationsQuery, useRecentConfigsQuery } from '../../api';
import { type AgentQuickStartContext, pickAgentQuickActions } from './agentQuickStartActions';
import { formatAgentHook, pickAgentHook } from './agentQuickStartHooks';

const QUICK_ACTION_COUNT = 3;

function AgentQuickStart() {
  // Every chip here starts a fresh conversation — a landing-page quick start is
  // a new question, not a continuation of whatever thread was last open.
  const { openNewAgentConversation } = useAgentPanel();
  // Newest item across services; render generic labels until (or unless) data resolves.
  const lastConfig = useRecentConfigsQuery({ limit: 1 }).data?.[0];
  const lastRun = useLastSimulationsQuery({ limit: 1 }).data?.[0];

  const configName = lastConfig?.name;
  const runName = lastRun?.run_name;
  const service = lastRun?.service;
  const context = useMemo<AgentQuickStartContext>(
    () => ({ configName, runName, service }),
    [configName, runName, service],
  );

  // Rolled once per mount (and again only when the personalizable data lands):
  // the tile stays stable while the user looks at it, but shows a different hook
  // and different chips on the next page load.
  const hook = useMemo(() => pickAgentHook(), []);
  const actions = useMemo(() => pickAgentQuickActions(QUICK_ACTION_COUNT, context), [context]);

  return (
    <Widget
      title="PA Agent"
      titleIcon={
        <Box
          component={AgentChatIcon}
          width={18}
          height={18}
          aria-hidden
          // Widget nudges title icons down by mt: 1 for its 24px MUI icons; this
          // 18px glyph needs that cancelled to sit on the title's centre line.
          sx={{ display: 'block', mt: -1 }}
        />
      }
    >
      <Stack spacing={1.5} sx={{ px: 1.5, py: 1.5 }}>
        <Typography variant="body2">{formatAgentHook(hook)}</Typography>
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
          {actions.map((action) => (
            <Chip
              key={action.key}
              label={action.label(context)}
              size="small"
              onClick={() => openNewAgentConversation(action.prompt(context))}
              sx={{ maxWidth: '100%' }}
            />
          ))}
          <Chip
            key="ask-anything"
            label="Ask something else…"
            size="small"
            onClick={() => openNewAgentConversation()}
            sx={{ maxWidth: '100%' }}
          />
        </Stack>
      </Stack>
    </Widget>
  );
}

export default AgentQuickStart;
